/** Speed units, and the one preference this portal remembers in the browser.
 *
 * The logger records and streams speed in km/h, the service stores it that
 * way, and every number on the wire stays in that unit: converting is the
 * reader's business, at the moment of display. A visitor who thinks in miles
 * clicks the unit once and the choice is kept in `localStorage`, which is the
 * right place for it — a convenience of this browser rather than a fact about
 * the account, so it needs no sign-in and no round trip. Storage can be
 * absent, full or forbidden (a private window, a locked-down browser), and a
 * page that cannot remember the preference still has to show a speed, so
 * every read and write is guarded and the default is the wire's own unit.
 */

export type SpeedUnit = 'kmh' | 'mph';

export const SPEED_UNITS: readonly SpeedUnit[] = ['kmh', 'mph'];

/** The international mile, exactly. */
export const KM_PER_MILE = 1.609344;

export const SPEED_UNIT_KEY = 'gt7sync.speedUnit';

/** What the wire carries, and what a reader with no stored choice sees. */
export const DEFAULT_SPEED_UNIT: SpeedUnit = 'kmh';

export function isSpeedUnit(value: unknown): value is SpeedUnit {
  return value === 'kmh' || value === 'mph';
}

/** A speed the logger measured, in the reader's unit. Unrounded: the caller
 *  decides how many digits its column shows. */
export function speedIn(kmh: number, unit: SpeedUnit): number {
  return unit === 'mph' ? kmh / KM_PER_MILE : kmh;
}

/** The short label after a figure. */
export function speedLabel(unit: SpeedUnit): string {
  return unit === 'mph' ? 'mph' : 'km/h';
}

/** The long form, for assistive technology. */
export function speedWords(unit: SpeedUnit): string {
  return unit === 'mph' ? 'miles per hour' : 'kilometres per hour';
}

export function otherSpeedUnit(unit: SpeedUnit): SpeedUnit {
  return unit === 'mph' ? 'kmh' : 'mph';
}

/** The slice of `Storage` this needs, so a test can pass a plain object and
 *  a browser with storage disabled can pass nothing at all. */
export type UnitStore = Pick<Storage, 'getItem' | 'setItem'>;

function browserStore(): UnitStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Reading `localStorage` itself throws where site data is blocked.
    return null;
  }
}

export function readSpeedUnit(store: UnitStore | null = browserStore()): SpeedUnit {
  if (!store) return DEFAULT_SPEED_UNIT;
  try {
    const raw = store.getItem(SPEED_UNIT_KEY);
    return isSpeedUnit(raw) ? raw : DEFAULT_SPEED_UNIT;
  } catch {
    return DEFAULT_SPEED_UNIT;
  }
}

/** Remembers the choice. False when the browser would not keep it, which is
 *  not an error: the page shows the unit either way. */
export function writeSpeedUnit(unit: SpeedUnit, store: UnitStore | null = browserStore()): boolean {
  if (!store) return false;
  try {
    store.setItem(SPEED_UNIT_KEY, unit);
    return true;
  } catch {
    return false;
  }
}
